import { formatDateTime, toPayloadDateBroker } from '@energy-stacks/shared';
import CardWidget from '../CardWidget';
import { useGetSessionNumberAndConsumptionSlicesQuery } from '@energy-stacks/broker/feature-dashboard-data';
import { Props } from 'react-apexcharts';
import { IconRecharging } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface ActiveSessionsWidgetProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export const ActiveSessionsWidget = ({
  startDate,
  endDate,
}: ActiveSessionsWidgetProps) => {
  const { t } = useTranslation('dashboard');

  const { data, isLoading } = useGetSessionNumberAndConsumptionSlicesQuery(
    {
      from: startDate ? toPayloadDateBroker(startDate) : undefined,
      to: endDate ? toPayloadDateBroker(endDate) : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const sessions =
    data?.timeSlices.map((transaction) => {
      const activeTransactions = transaction.activeSessions;
      const dateSlice = transaction.timesliceStart;
      return {
        activeSessions: Math.round(activeTransactions),
        timestamp: formatDateTime(dateSlice),
      };
    }) || [];
  const maxNumberofSessions = Math.max(
    ...sessions.map((session) => session.activeSessions)
  );

  const currentActiveSessions = Math.round(
    data?.distinctActiveSessionsCount ?? 0
  );

  const chartData: Props = {
    type: 'line',
    height: 90,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        sparkline: {
          enabled: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        padding: {
          top: 7,
          bottom: 7,
          left: 7,
          right: 7,
        },
      },
      colors: ['#fff'],
      fill: {
        type: 'solid',
        opacity: 1,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      yaxis: {
        min: 0,
        max: maxNumberofSessions || 100,
      },
      tooltip: {
        theme: 'dark',
        fixed: {
          enabled: false,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName: string) => seriesName,
          },
          formatter: (val: number) => Math.floor(val) + '',
        },
        marker: {
          show: false,
        },
      },
    },
    series: [
      {
        name: t('activeSessions'),
        data: sessions.map((session) => {
          return {
            x: session.timestamp,
            y: session.activeSessions,
          };
        }),
      },
    ],
  };

  return (
    <CardWidget
      chartData={chartData}
      color="purple"
      isLoading={isLoading}
      value={currentActiveSessions}
      name={t('activeSessions')}
      icon={<IconRecharging />}
    />
  );
};
